import { Send } from "@tamagui/lucide-icons";
import { useEffect, useRef, useState } from "react";

import { useGetSmsTimeline, useSendSmsMessage } from "@/api/generated";
import type { SmsTimelineMessage } from "@/api/generated/models/SmsTimelineMessage";
import {
  Button,
  CloseButton,
  Heading,
  Input,
  LoadingSpinner,
  Paragraph,
  ScrollView,
  Text,
  XStack,
  YStack,
} from "@/components";
import ErrorLabel from "@/components/forms/ErrorLabel";

// The reply is produced asynchronously by ProcessSMSMessageWorkflow, so poll
// while the panel is open to surface the inbound message + reply as they land.
const POLL_INTERVAL_MS = 4000;
// Safety cap so the input never stays locked if a reply is suppressed/never sent.
const REPLY_TIMEOUT_MS = 60000;

const formatTimestamp = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const MessageBubble = ({ message }: { message: SmsTimelineMessage }) => {
  const isInbound = message.direction === "inbound";
  return (
    <YStack
      gap="$50"
      maxWidth="85%"
      alignSelf={isInbound ? "flex-end" : "flex-start"}
    >
      <YStack
        backgroundColor={isInbound ? "$fillPatient" : "$fillClinician"}
        paddingVertical="$100"
        paddingHorizontal="$150"
        borderRadius="$150"
      >
        <Paragraph
          size="$sm"
          color={isInbound ? "$textPatient" : "$textPrimary"}
        >
          {message.body}
        </Paragraph>
      </YStack>
      <XStack
        gap="$100"
        alignSelf={isInbound ? "flex-end" : "flex-start"}
        alignItems="center"
      >
        {!isInbound && (
          <Text size="$xs" color="$textSecondary">
            {message.kind}
          </Text>
        )}
        <Text size="$xs" color="$textSecondary">
          {formatTimestamp(message.timestamp)}
        </Text>
      </XStack>
    </YStack>
  );
};

const SmsTimeline = ({ onClose }: { onClose: () => void }) => {
  const { data, isLoading, isError } = useGetSmsTimeline({
    query: { refetchInterval: POLL_INTERVAL_MS },
  });
  const messages = data?.messages ?? [];

  const scrollRef = useRef<any>(null);
  const [draft, setDraft] = useState("");
  // Optimistically shown sent messages — the real inbound event is persisted
  // asynchronously by the workflow and only arrives on a later poll, so show
  // the user's text immediately and drop it once the server timeline has it.
  const pendingIdRef = useRef(0);
  const [pending, setPending] = useState<{ id: number; body: string }[]>([]);
  const inboundBaselineRef = useRef<number | null>(null);

  // Lock the input from send until the agent's reply (a new outbound message)
  // arrives. Cleared either by a new outbound or a safety timeout.
  const [awaitingReply, setAwaitingReply] = useState(false);
  const outboundBaselineRef = useRef(0);
  const replyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const outboundCount = messages.filter(
    (m) => m.direction === "outbound",
  ).length;

  useEffect(() => {
    const inboundCount = (data?.messages ?? []).filter(
      (m) => m.direction === "inbound",
    ).length;
    setPending((prev) => {
      // Nothing outstanding: keep the baseline level with the server.
      if (prev.length === 0) {
        inboundBaselineRef.current = inboundCount;
        return prev;
      }
      const baseline = inboundBaselineRef.current ?? inboundCount;
      const confirmed = Math.max(0, inboundCount - baseline);
      if (confirmed === 0) return prev;
      // Each new inbound confirms the oldest still-pending send.
      inboundBaselineRef.current = inboundCount;
      return prev.slice(confirmed);
    });
  }, [data]);

  // Unlock once a new outbound (the reply) lands.
  useEffect(() => {
    if (awaitingReply && outboundCount > outboundBaselineRef.current) {
      setAwaitingReply(false);
      if (replyTimeoutRef.current) clearTimeout(replyTimeoutRef.current);
    }
  }, [awaitingReply, outboundCount]);

  useEffect(() => {
    return () => {
      if (replyTimeoutRef.current) clearTimeout(replyTimeoutRef.current);
    };
  }, []);

  // Scroll to the newest message whenever the thread grows (sent or received).
  const threadCount = messages.length + pending.length;
  useEffect(() => {
    if (threadCount === 0) return;
    requestAnimationFrame(() =>
      scrollRef.current?.scrollToEnd?.({ animated: true }),
    );
  }, [threadCount]);

  const { mutate: sendSmsMessage, isPending: isSending } = useSendSmsMessage();

  const send = () => {
    const message = draft.trim();
    if (!message || isSending || awaitingReply) return;
    setDraft("");
    pendingIdRef.current += 1;
    setPending((prev) => [
      ...prev,
      { id: pendingIdRef.current, body: message },
    ]);
    outboundBaselineRef.current = outboundCount;
    setAwaitingReply(true);
    if (replyTimeoutRef.current) clearTimeout(replyTimeoutRef.current);
    replyTimeoutRef.current = setTimeout(
      () => setAwaitingReply(false),
      REPLY_TIMEOUT_MS,
    );
    sendSmsMessage({ data: { message } });
  };

  return (
    <YStack flex={1} backgroundColor="$fillModal">
      <YStack
        gap="$200"
        paddingHorizontal="$200"
        paddingTop="$200"
        paddingBottom="$200"
        borderBottomWidth={1}
        borderBottomColor="$strokeNeutral"
      >
        <CloseButton onClose={onClose} dataTrackingId="sms-timeline-close" />
        <YStack gap="$50">
          <Heading size="$lg">SMS thread</Heading>
          <Paragraph size="$sm" color="$textSecondary">
            Inbound SMS merged with every outbound SMS notification from the
            last 30 days, oldest first. Dev view only.
          </Paragraph>
        </YStack>
      </YStack>

      <ScrollView
        ref={scrollRef}
        flex={1}
        contentContainerStyle={{
          paddingHorizontal: "$200",
          paddingVertical: "$200",
        }}
      >
        {isLoading && (
          <XStack justifyContent="center" paddingVertical="$300">
            <LoadingSpinner />
          </XStack>
        )}

        {isError && (
          <ErrorLabel>Could not load the SMS timeline. Try again.</ErrorLabel>
        )}

        {!isLoading &&
          !isError &&
          messages.length === 0 &&
          pending.length === 0 && (
            <Paragraph size="$sm" color="$textSecondary">
              No SMS messages yet for this user.
            </Paragraph>
          )}

        <YStack gap="$150">
          {messages.map((message, idx) => (
            <MessageBubble
              key={`${message.timestamp}-${idx}`}
              message={message}
            />
          ))}
          {pending.map((p) => (
            <YStack
              key={`pending-${p.id}`}
              gap="$50"
              maxWidth="85%"
              alignSelf="flex-end"
              opacity={0.6}
            >
              <YStack
                backgroundColor="$fillPatient"
                paddingVertical="$100"
                paddingHorizontal="$150"
                borderRadius="$150"
              >
                <Paragraph size="$sm" color="$textPatient">
                  {p.body}
                </Paragraph>
              </YStack>
              <Text size="$xs" color="$textSecondary" alignSelf="flex-end">
                Sending…
              </Text>
            </YStack>
          ))}
        </YStack>
      </ScrollView>

      <YStack
        padding="$200"
        gap="$100"
        backgroundColor="$fillModal"
        borderTopWidth={1}
        borderTopColor="$strokeNeutral"
      >
        <Input
          placeholder={
            awaitingReply ? "Waiting for reply…" : "Send an SMS as the user"
          }
          value={draft}
          onChangeText={setDraft}
          onSubmitEditing={send}
          disabled={isSending || awaitingReply}
          postfix={
            <Button.Wrapper
              padding="$75"
              round
              disabled={!draft.trim() || isSending || awaitingReply}
              backgroundColor={
                !draft.trim() || isSending || awaitingReply
                  ? "$backgroundGround"
                  : "$fillButtonPrimary"
              }
              onPress={send}
              aria-label="Send SMS message"
            >
              {isSending || awaitingReply ? (
                <LoadingSpinner size={16} />
              ) : (
                <Send
                  size={16}
                  color={!draft.trim() ? "$textPrimary" : "$iconCta"}
                />
              )}
            </Button.Wrapper>
          }
        />
      </YStack>
    </YStack>
  );
};

export default SmsTimeline;
